import * as _ from 'underscore';

import { Helpers } from './helpers/Helpers';

/**
 * Takes any feature flags specified in the URL, and parses them into an array of objects that can
 * be used to override the default values. Feature flag overrides must be specified in the query
 * string (between ? and #) portion of the URL.
 *
 * Overrides work as follows:
 *  - If a flag is not given, it will not be overridden
 *  - If a flag has any value that begins with "t", "y", or "1", it will be set to true
 *  - If a flag has any value that begins with "f", "n", or "0", it will be set to false
 *  - If a flag has any value other than those above, it will not be overridden
 */
export module FeatureFlagsHelper {

  interface FeatureFlagOverride {
    flagName: string;
    value: boolean;
  }

  /**
   * Returns an object that represents the overridden feature flags
   */
  export function getOverridesFromQueryString(queryString: string): _.Dictionary<boolean> {
    const params: string[] = queryString.slice(1).split('&');
    const overrides: _.Dictionary<boolean> = {};

    params.forEach((param: string) => {
      const override: FeatureFlagOverride = parseQueryParameter(param);
      if (isOverrideValid(override)) {
        overrides[override.flagName] = override.value;
      }
    });

    return overrides;
  }

  function isOverrideValid(override: FeatureFlagOverride) {
    return override && override.flagName
      && (override.value === true || override.value === false);
  }

  function parseQueryParameter(param: string): FeatureFlagOverride {
    const keyAndValue: string[] = param.split('=');
    const key: string = decodeURIComponent(keyAndValue[0]);
    const valueString: string = decodeURIComponent(keyAndValue[1]);

    let value: boolean = null;
    if (Helpers.BooleanQueryParamRegex.test(valueString)) {
      value = true;
    } else if (Helpers.BooleanQueryParamRegexFalse.test(valueString)) {
      value = false;
    }

    return { flagName: key, value: value };
  }

  const featureFlagsOverwrittenFromQueryString: _.Dictionary<string> = {};

  /**
   * Takes in a query string and overwrites the feature flags in the VizPortal module according
   * to the overrides specified in the query string.
   */
  export function overwriteFeatureFlagsFromQueryString(queryString: string, featureFlagsObj: any) {
    const overrides: _.Dictionary<boolean> = getOverridesFromQueryString(queryString);

    _.each(overrides, (value: boolean, flagName: string) => {
      // Don't add flags that we haven't defined
      if (isFlagDefined(flagName, featureFlagsObj)) {
        featureFlagsObj[flagName] = value;
        featureFlagsOverwrittenFromQueryString[flagName] = flagName;
      }
    });
  }

  /**
   * Takes the server feature flags and updates the corresponding client feature flags to match.
   * Values specified in the query string take precedence over values from the server.
   */
  export function overwriteFeatureFlagsFromServerFeatureFlags(serverFeatureFlags: any, featureFlagsObj: any): void {
    _.each(serverFeatureFlags, (value: boolean, flagName: string) => {
      // Don't add flags that we haven't defined and query string overrides take priority over flag values from the server
      if (isFlagDefined(flagName, featureFlagsObj) && !featureFlagsOverwrittenFromQueryString[flagName]) {
        featureFlagsObj[flagName] = value;
      }
    });
  }

  function isFlagDefined(flag: string, featureFlagsObj: any) {
    return featureFlagsObj[flag] !== undefined;
  }

}
